import { PartConfiguratorTopLevelView } from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorTopLevelView.tsx";
import {
	PART_CONFIGURATION_NOT_SAVED,
	PartConfiguratorTreeResult,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatefulView.tsx";
import { ConfigurationCreationApi } from "src/api/generated/erp/configurator/api/configurationCreationApi";
import { PartConfigurationDetailsView } from "src/components/views/erp/configurator/inspecting/PartConfigurationDetailsView.tsx";
import i18n from "i18next";
import { CloseDialogFunc, useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { askObjectConfigurationHistoryEntryInput } from "src/components/views/erp/configurator/objectConfigurationHistory/ObjectConfigurationHistoryEntryInputForm.utils.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { useAavoToast } from "src/components/common/toast/AavoToastContext";
import { CustomerOrderOfferLinePartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/impl/customerOrderOfferLinePartConfiguratorType.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getPartConfiguratorAgentContextData } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeUtils.ts";
import { useCustomerOfferLineSubConfigurationImportAction } from "src/components/views/erp/configurator/configuratorForm/impl/useCustomerOfferLineSubConfigurationImportAction.tsx";
import { CustomerOrderOfferState } from "src/api/generated/erp/db/types/enums/customerOrderOfferState.ts";
import { ConfigurationCheckpointApi } from "src/api/generated/erp/configurator/checkpoints/api/configurationCheckpointApi.ts";
import { CustomerOrderChangeForm } from "src/components/views/erp/sales/customerOrderChange/CustomerOrderChangeForm.tsx";
import { plainTextToOrderChangeContent } from "src/components/views/erp/sales/customerOrderOffer/customerOfferOrderChanges.ts";
import { showAsyncDialog } from "src/components/common/dialogs/asyncDialog.ts";

export interface CustomerOrderOfferLineConfiguratorViewProps {
	configuratorType: CustomerOrderOfferLinePartConfiguratorType;
	customerOfferId: number;
	customerOfferState: CustomerOrderOfferState;
	closeDialog: CloseDialogFunc;
	refreshSourceView?: () => void;
	disabled?: boolean;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

export const CustomerOrderOfferLineConfiguratorView = ({
	configuratorType,
	customerOfferId,
	customerOfferState,
	closeDialog,
	refreshSourceView,
	disabled,
	onDataDirtyStateChanged,
}: CustomerOrderOfferLineConfiguratorViewProps) => {
	const { openDialog } = useGenericDialog();
	const { showToast } = useAavoToast();
	const hasPermission = useHasPermission();
	const canOpenConfigurationDetails = hasPermission(Permissions.Sales.CustomerOffers.READ_COSTS);
	const customerOrderOfferLineId = configuratorType.customerOrderOfferLineId;
	const subConfigurationImportAction = useCustomerOfferLineSubConfigurationImportAction({
		customerOfferId: customerOfferId,
		currentCustomerOfferLineId: customerOrderOfferLineId,
	});

	return (
		<PartConfiguratorTopLevelView
			configuratorType={configuratorType}
			subConfigurationImportAction={subConfigurationImportAction}
			onDataDirtyStateChanged={onDataDirtyStateChanged}
			onCompleted={async (result) => {
				if (result.type === "success") {
					return customerOfferState === "ACCEPTED" ?
							await onSubmitForAcceptedOffer(result.value)
						:	await onSubmit(result.value);
				} else {
					await closeDialog?.({ confirmIfEdited: true });
				}
			}}
			readonly={disabled}
		/>
	);

	async function onSubmit({
		propertyValues,
		propertyInputValues,
		configurationSessionId,
		configuratorInput,
		checkpointState,
	}: PartConfiguratorTreeResult) {
		const historyEntryInput = await askObjectConfigurationHistoryEntryInput({
			openDialog,
			objectType: "CUSTOMER_ORDER_OFFER_LINE",
			objectId: customerOrderOfferLineId,
			configurationSessionId,
			configuratorType: configuratorType,
			configuratorInput,
			checkpointState,
		});
		if (historyEntryInput === undefined) return PART_CONFIGURATION_NOT_SAVED;

		const partConfigurationId = await ConfigurationCreationApi.createPartConfigurationForCustomerOrderOfferLine({
			customerOrderOfferLineId: customerOrderOfferLineId,
			propertyValues: propertyValues,
			propertyInputValues: propertyInputValues,
			configurationSessionId: configurationSessionId,
			historyEntryInput: historyEntryInput,
			testContextEnabled: configuratorType.testContextEnabled,
		});

		await closeDialog();
		refreshSourceView?.();

		if (canOpenConfigurationDetails) {
			openDialog(() => ({
				title: i18n.t("configuration"),
				content: (
					<PartConfigurationDetailsView
						partConfigurationId={partConfigurationId}
						partConfiguratorType={configuratorType}
					/>
				),
				agentContext: {
					key: defineAgentUiContextKey("erp.configurator.customerOrderOfferLinePartConfiguration.dialog"),
					title: i18n.t("configuration"),
					data: {
						...getPartConfiguratorAgentContextData(configuratorType),
						partConfigurationId: partConfigurationId,
					},
				},
			}));
		} else {
			showToast({ title: i18n.t("configuration_created") });
		}
	}

	// On an accepted offer the configuration is set to the line together with an order change. The salesperson
	// gives only the subject; the AI-generated description and calculated cost and price changes are read-only.
	async function onSubmitForAcceptedOffer({
		propertyValues,
		propertyInputValues,
		configurationSessionId,
		configuratorInput,
		checkpointState,
	}: PartConfiguratorTreeResult) {
		const [configuration, description] = await Promise.all([
			ConfigurationCreationApi.createPartConfigurationForAcceptedCustomerOfferLine({
				customerOrderOfferLineId: customerOrderOfferLineId,
				configurationSessionId: configurationSessionId,
				propertyValues: propertyValues,
				propertyInputValues: propertyInputValues,
				testContextEnabled: configuratorType.testContextEnabled,
			}),
			// The description is generated from the component form's input and checkpoints, which a property-model
			// form does not have; the salesperson writes it instead.
			propertyInputValues != null ?
				Promise.resolve("")
			:	ConfigurationCheckpointApi.generateHistoryDescription({
					configurationSessionId: configurationSessionId,
					configuratorType: configuratorType,
					configuratorInput: configuratorInput,
					latestCheckpointPropertyValues: checkpointState.previousPropertyValues,
					checkpoints: checkpointState.entries.map((entry) => ({
						description: entry.description,
						price: entry.price,
					})),
				}),
		]);

		// The configurator stays open until the order change is saved, so cancelling returns to the configurator.
		const customerOrderChangeId = await showAsyncDialog<number>(openDialog, ({ onCompleted, onFormEdited }) => ({
			title: i18n.t("order_change"),
			size: "md",
			content: (
				<CustomerOrderChangeForm
					objectType={"CUSTOMER_OFFER"}
					objectId={customerOfferId}
					existingCustomerOrderChange={undefined}
					subjectOnly
					editableContent={propertyInputValues != null}
					defaultValueOverrides={{
						content: plainTextToOrderChangeContent(description),
						costChange: configuration.costChange,
						priceChange: configuration.priceChange,
					}}
					submitOverride={(values) =>
						ConfigurationCreationApi.setAcceptedCustomerOfferLinePartConfiguration({
							customerOrderOfferLineId: customerOrderOfferLineId,
							partConfigurationId: configuration.partConfigurationId,
							historyEntryInput: { keepCurrentEntry: true, comment: description },
							orderChangeSubject: values.subject,
							orderChangeContent: values.content,
						})
					}
					onFormEdited={onFormEdited}
					onCompleted={onCompleted}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.acceptedCustomerOfferLineOrderChange.dialog"),
				title: i18n.t("order_change"),
				data: {
					...getPartConfiguratorAgentContextData(configuratorType),
					customerOfferId,
					partConfigurationId: configuration.partConfigurationId,
				},
			},
		}));
		if (customerOrderChangeId === undefined) return PART_CONFIGURATION_NOT_SAVED;

		await closeDialog();
		refreshSourceView?.();
		showToast({ title: i18n.t("configuration_created") });
	}
};
